import { type JSX, type ReactNode } from 'react';

import { Box, Button, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';

import type { PlaceDto } from '@/api/types/Place';

import { PhoneText } from './PhoneText';
import { PlaceTypeChip } from './PlaceTypeChip';
import { usePlaceCopy } from './usePlaceCopy';

// **SEPARATE component tree from the desktop table** per NF-05 / ADR-0029
// (CLAUDE.md: never one CSS-collapsed responsive table). Mirrors the
// `OrdersCards` conventions — `.oc-row` dashed label/value rows, uppercase 11px
// label column, an `.oc-footer` action strip — carrying the CHR-06 fields
// (`prototyp.html#s-mobile`).
//
// The footer action is „Kopírovat adresu" full-width ≥ 44 px: a dispatcher
// standing at a truck copies the address into an SMS, which is the whole point
// of the mobile view. Phones inside Kontakt / Pozn. are `tel:` links.

const TAP_TARGET_MIN_PX = 44;
const CARD_GAP_FACTOR = 1.25;
const SKELETON_CARDS = 3;
const SKELETON_CARD_HEIGHT_PX = 196;

const labelSx = {
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  fontWeight: 600,
  minWidth: 76,
  pt: '2px',
};

export interface PlacesCardsProps {
  places: PlaceDto[];
  // Fetch state, so an in-flight FIRST load renders skeletons instead of the
  // empty state — „no rows yet" and „no rows at all" are different things.
  status: 'idle' | 'loading' | 'loadingMore' | 'ready' | 'error';
  // Viewer (ADR-0046) — read-only: the card renders without „Upravit".
  canEdit: boolean;
  onEdit: (id: string) => void;
  // Shared `PlacesEmptyState`, rendered when the ready list is empty.
  emptyState?: ReactNode;
}

export const PlacesCards = ({
  places,
  status,
  canEdit,
  onEdit,
  emptyState,
}: PlacesCardsProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation('places');
  // Copy behaviour lives in the shared hook — identical format, toast and 2 s
  // check window as the desktop table.
  const { copiedId, copyPlace } = usePlaceCopy();

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(CARD_GAP_FACTOR),
      }}
      data-testid="places-cards-root"
    >
      {/* FIRST load — skeleton cards. A refetch keeps the current cards on
          screen (no shimmer appended below live data). */}
      {status === 'loading' && places.length === 0
        ? Array.from({ length: SKELETON_CARDS }, (_, i) => (
            <Skeleton
              key={`place-card-skeleton-${i}`}
              variant="rectangular"
              height={SKELETON_CARD_HEIGHT_PX}
              sx={{ borderRadius: 3 }}
              data-testid="places-cards-skeleton"
            />
          ))
        : null}

      {status === 'ready' && places.length === 0 ? emptyState : null}

      {places.map((place) => {
        const isCopied = copiedId === place.id;
        return (
          <Box
            key={place.id}
            data-testid={`place-card-${place.id}`}
            data-type={place.type}
            sx={{
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3, // 12px (DESIGN.md §4 card radius)
              boxShadow: theme.elevation.shadow1,
              overflow: 'hidden',
            }}
          >
            {/* `.och` — name + type chip on the left, „Upravit" on the right. */}
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={1.25}
              sx={{ px: theme.spacing(1.75), pt: theme.spacing(1.5), pb: theme.spacing(0.75) }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 14,
                    color: theme.palette.text.primary,
                  }}
                >
                  {place.name}
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <PlaceTypeChip
                    type={place.type}
                    data-testid={`place-card-type-${place.id}`}
                  />
                </Box>
              </Box>
              {canEdit ? (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => onEdit(place.id)}
                  data-testid={`place-card-edit-${place.id}`}
                  sx={{ minHeight: TAP_TARGET_MIN_PX, flexShrink: 0 }}
                >
                  {t('action.edit')}
                </Button>
              ) : null}
            </Stack>

            {/* `.oc-row` — Adresa / Kontakt / Pozn. */}
            <CardRow label={t('card.address')}>
              <Typography component="span" sx={{ fontSize: 13 }}>
                {place.address?.trim() ? (
                  place.address
                ) : (
                  <Box component="span" sx={{ color: theme.palette.text.disabled }}>
                    {t('value.addressEmpty')}
                  </Box>
                )}
              </Typography>
            </CardRow>
            <CardRow label={t('card.contact')}>
              <PhoneText
                text={place.contact}
                dial
                data-testid={`place-card-contact-${place.id}`}
              />
            </CardRow>
            <CardRow label={t('card.note')} muted>
              <PhoneText
                text={place.note}
                dial
                data-testid={`place-card-note-${place.id}`}
              />
            </CardRow>

            {/* `.oc-footer` — full-width „Kopírovat adresu" ≥ 44 px. */}
            <Box
              sx={{
                display: 'flex',
                gap: theme.spacing(1),
                px: theme.spacing(1.75),
                py: theme.spacing(1.25),
                borderTop: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.background.default,
              }}
            >
              <Button
                variant="outlined"
                fullWidth
                startIcon={isCopied ? <CheckIcon /> : <ContentCopyOutlinedIcon />}
                onClick={() => void copyPlace(place)}
                data-testid={`place-card-copy-${place.id}`}
                data-copied={isCopied ? 'true' : 'false'}
                sx={{
                  minHeight: TAP_TARGET_MIN_PX,
                  justifyContent: 'center',
                  borderColor: isCopied ? theme.palette.primary.main : theme.borderStrong,
                  color: isCopied ? theme.palette.primary.main : theme.palette.text.primary,
                }}
              >
                {isCopied ? t('action.copyDone') : t('action.copy')}
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// One `.oc-row`: dashed top border, uppercase label column, free-form value.
const CardRow = ({
  label,
  muted = false,
  children,
}: {
  label: string;
  muted?: boolean;
  children: ReactNode;
}): JSX.Element => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      gap={1.25}
      sx={{
        px: theme.spacing(1.75),
        py: theme.spacing(1),
        borderTop: `1px dashed ${theme.palette.divider}`,
        fontSize: 13,
        color: muted ? theme.palette.text.secondary : theme.palette.text.primary,
      }}
    >
      <Typography component="span" sx={{ ...labelSx, color: theme.palette.text.secondary }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    </Stack>
  );
};
